import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const Privacy = () => {
  return (
    <div className="privacy-container">
      {/* SEO Metadata */}
      <Helmet>
        <title>Privacy Policy - GhrianGhraffy</title>
        <meta
          name="description"
          content="Privacy Policy for GhrianGhraffy, explaining how we collect, use, and protect personal data for schools, staff and parents."
        />
      </Helmet>

      <h1>Privacy Policy</h1>
      <p>Last updated: July 25, 2025</p>

      <section>
        <h2>Introduction</h2>
        <p>
          Welcome to GhrianGhraffy, an app for School Principals and School Staff to capture,
          upload, and view their school&apos;s photos securely and quickly. Parents have
          view-only access. We prioritise your privacy by ensuring photos are not stored on
          your personal device after they&apos;ve been uploaded. This Privacy Policy explains
          how we collect, use, disclose, and protect your information when you use our services
          (including our mobile app and web dashboard). By using GhrianGhraffy, you agree to
          the terms outlined in this policy.
        </p>
      </section>

      <section>
        <h2>Information We Collect</h2>
        <p>We collect the following types of information:</p>
        <ul>
          <li>
            <strong>Account and Profile Information</strong>: When you log in, we collect your
            email, password (stored only in hashed form on our servers), and an authentication
            token stored locally (for example, in your browser&apos;s local storage or mobile
            secure storage). Profile details, such as your role (e.g., Staff, Principal, Parent,
            Admin), display name, and school name, are fetched from our servers.
          </li>
          <li>
            <strong>Photos and Media</strong>: When you capture photos with the camera or select
            them from your gallery, they may be temporarily stored in the app&apos;s cache on
            your device. If you save a draft (e.g., when offline), photos are stored in the app&apos;s
            local storage but are automatically deleted after the draft is uploaded or cancelled.
            Uploaded photos are sent to our servers and removed from the app&apos;s local storage
            to help ensure teachers&apos; personal phones are not used as permanent storage for
            school photos. Photos already stored in your device&apos;s own gallery remain
            under your device&apos;s control.
          </li>
          <li>
            <strong>Drafts</strong>: Drafts, including photos and associated data (e.g., story
            maps), are stored locally when saved offline. When you go online, drafts are
            automatically uploaded to our servers, and the photos and draft data are deleted
            from the app&apos;s local storage. Drafts older than 30 days are automatically
            deleted to prevent unnecessary storage.
          </li>
          <li>
            <strong>Device and Usage Information</strong>: We collect basic technical
            information such as network status (online/offline) and general app usage
            information to manage offline functionality and improve app performance. Additional
            device details (e.g., device type, operating system) may be collected by third-party
            services where used (see &quot;Third-Party Services&quot; below).
          </li>
        </ul>
      </section>

      <section>
        <h2>Cookies and Similar Technologies</h2>
        <p>
          On the GhrianGhraffy website and web dashboard, we may use cookies and similar
          technologies to:
        </p>
        <ul>
          <li>Keep you signed in and secure your session.</li>
          <li>Remember your preferences (for example, interface settings).</li>
          <li>
            Understand how the service is used so we can improve it (for example, basic
            analytics).
          </li>
        </ul>
        <p>
          You can usually control cookies through your browser settings. Disabling certain
          cookies may affect core functionality such as logging in or staying signed in.
        </p>
        {/* If you start using Google Analytics or ad networks, expand this section to name them explicitly. */}
      </section>

      <section>
        <h2>How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>
            Authenticate your account and provide access to role-specific features (e.g., photo
            uploads for Staff/Principal, viewing for Parents).
          </li>
          <li>
            Process photos and drafts for upload, viewing, and hashtag generation (e.g., based on
            school name or display name), while ensuring photos are removed from local app
            storage after upload or cancellation where possible.
          </li>
          <li>
            Improve app functionality and user experience based on network status and usage data.
          </li>
          <li>
            Communicate with you about your account, including security notifications and service
            updates.
          </li>
        </ul>
      </section>

      <section>
        <h2>Data Storage and Security</h2>
        <ul>
          <li>
            <strong>Local Storage</strong>: Photos are temporarily stored in the app&apos;s cache
            during capture or selection. Drafts are stored locally but are deleted after upload
            or cancellation to minimise storage on your device. Authentication tokens and basic
            profile details (such as school name) may be stored locally to keep you signed in.
          </li>
          <li>
            <strong>Server Storage</strong>: Uploaded photos and profile data are sent to our
            servers (
            <a href="https://ghrianghraffy.com">https://ghrianghraffy.com</a>) over HTTPS for
            secure transmission. We apply reasonable technical and organisational measures to
            protect this data against unauthorised access, loss, or misuse.
          </li>
          <li>
            We work to protect your information but no system is completely secure. You use the
            app at your own risk and should keep your login details confidential.
          </li>
        </ul>
      </section>

      <section>
        <h2>Data Sharing</h2>
        <p>We do not sell your personal information. We may share information:</p>
        <ul>
          <li>
            Within your school community, as part of normal use of the app (for example, staff
            uploading photos for Principals to review, or parents viewing approved photos).
          </li>
          <li>
            With service providers who help us operate the app (such as hosting, email delivery
            or analytics providers), under appropriate contracts and data protection safeguards.
          </li>
          <li>To comply with legal obligations or to protect our rights and the safety of users.</li>
        </ul>
      </section>

      <section>
        <h2>Third-Party Services</h2>
        <p>
          We may use trusted third-party service providers to help deliver and improve
          GhrianGhraffy. These may include:
        </p>
        <ul>
          <li>Cloud hosting and infrastructure providers.</li>
          <li>Email delivery or communication services.</li>
          <li>Optional analytics tools to understand how the service is used.</li>
        </ul>
        <p>
          These providers only receive the information necessary to perform their services and
          are required to handle your data securely and in line with applicable data protection
          laws. They are not allowed to use your information for their own independent purposes.
        </p>
      </section>

      <section>
        <h2>Your Rights</h2>
        <p>
          Depending on your region (for example, GDPR for EU/UK users or CCPA for California
          users), you may have rights to access, correct, or delete your personal data, or to
          object to or restrict certain types of processing. You can exercise these rights by
          contacting us at{" "}
          <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a>. We will
          respond within a reasonable timeframe and within any time limits required by law.
        </p>
      </section>

      <section>
        <h2>GDPR and Data Protection</h2>
        <p>
          For users in the European Economic Area (EEA) and the United Kingdom, we aim to process
          personal data in accordance with the General Data Protection Regulation (&quot;GDPR&quot;)
          and applicable local data protection laws.
        </p>

        <h3>Our role</h3>
        <ul>
          <li>
            For account information (for example, your login details, contact email and billing
            data), GhrianGhraffy acts as a <strong>data controller</strong>.
          </li>
          <li>
            For pupil photos and related content uploaded by schools and authorised staff, we
            generally act as a <strong>data processor</strong> on behalf of the school, which is
            the data controller. We process this data only according to the school&apos;s
            instructions and our agreements with them.
          </li>
        </ul>

        <h3>Legal bases for processing</h3>
        <p>We rely on one or more of the following legal bases to process your personal data:</p>
        <ul>
          <li>
            <strong>Contract</strong>: where processing is necessary to provide our services to
            you (for example, to create and manage your account, or allow you to log in and use
            the app).
          </li>
          <li>
            <strong>Legitimate interests</strong>: where we have a legitimate business interest
            that is not overridden by your rights and interests (for example, securing our
            systems, preventing abuse, or improving the service).
          </li>
          <li>
            <strong>Legal obligation</strong>: where we must process or retain data to comply
            with legal requirements (for example, tax or accounting rules).
          </li>
          <li>
            <strong>Consent</strong>: in limited cases where we ask for your explicit consent for
            a specific purpose. You can withdraw consent at any time where it is the legal basis
            for processing.
          </li>
        </ul>

        <h3>Your GDPR rights</h3>
        <p>
          Under GDPR, you may have the right to request access to your personal data, rectify
          inaccurate data, request deletion, restrict or object to certain processing, and (in some
          cases) request data portability. You also have the right to lodge a complaint with your
          local data protection authority.
        </p>
        <p>
          You can exercise these rights by contacting us at{" "}
          <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a>. We may need to
          verify your identity before responding to your request.
        </p>

        <h3>Data transfers</h3>
        <p>
          If we transfer personal data outside of the EEA/UK (for example, to service providers),
          we will take appropriate steps to protect it, such as using the EU/UK Standard
          Contractual Clauses or relying on other lawful transfer mechanisms where required.
        </p>

        <h3>Retention</h3>
        <p>
          We keep personal data only for as long as necessary to provide the service, fulfil the
          purposes described in this Privacy Policy, and meet our legal obligations. For example,
          data associated with trial accounts may be deleted after the trial ends if no
          subscription is started, in line with our product design.
        </p>
      </section>

      <section>
        <h2>Children’s Privacy</h2>
        <p>
          GhrianGhraffy is intended to be used by schools, their authorised staff, and parents.
          The service may involve photos of children, but we do not offer accounts directly to
          children or allow children to sign up themselves.
        </p>
        <p>
          Schools and authorised staff are responsible for ensuring they have the appropriate
          legal basis (for example, consent or another lawful ground under local law) to capture
          and upload photos of pupils. We process this data on behalf of the school in line with
          this Privacy Policy and our agreements with the school.
        </p>
        <p>
          If you believe we have collected personal information directly from a child without
          proper authorisation, please contact us at{" "}
          <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a> so we can
          investigate and take appropriate action.
        </p>
      </section>

      <section>
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our
          practices, technologies, or legal requirements. If we make significant changes, we will
          notify you via the app, our website, or email where appropriate. The latest version
          will always be available on this page.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or how we handle personal data, contact
          us at{" "}
          <a href="mailto:privacy@ghrianghraffy.com">privacy@ghrianghraffy.com</a>.
        </p>
      </section>
    </div>
  );
};

export default Privacy;

